import React, { useState, useEffect } from "react"
import {
  Box,
  Button,
  TextField,
  Typography,
  Card,
  CardContent,
} from "@mui/material"
import { apiFetch } from "@/hooks/apiFetch"
import { toast } from "sonner"

const WebsiteConfig = () => {
  // Email config states
  const [emailConfig, setEmailConfig] = useState({
    mailer: "smtp",
    host: "",
    port: "587",
    username: "",
    password: "",
    encryption: "tls",
    from_address: "",
  })
  const [emailLoading, setEmailLoading] = useState(false)

  // Website details states
  const [title, setTitle] = useState("")
  const [description, setDescription] = useState("")
  const [logo, setLogo] = useState<File | null>(null)
  const [banner, setBanner] = useState<File | null>(null)
  // URLs for displaying existing images
  const [logoUrl, setLogoUrl] = useState("")
  const [bannerUrl, setBannerUrl] = useState("")
  const [detailsLoading, setDetailsLoading] = useState(false)

  useEffect(() => {
    fetchSettings()
  }, [])

  const fetchSettings = async () => {
    try {
      const data = await apiFetch<any>("settings/email-config", {
        method: "GET",
      })
      if (data) {
        setEmailConfig({
          mailer: data.mailer || "smtp",
          host: data.host || "",
          port: data.port || "587",
          username: data.username || "",
          password: data.password || "",
          encryption: data.encryption || "tls",
          from_address: data.from_address || data.email || "",
        })
      }
    } catch (error) {
      console.error("Failed to fetch email settings", error)
    }

    try {
      const data = await apiFetch<any>("settings/website-details", {
        method: "GET",
      })
      if (data) {
        setTitle(data.title || "")
        setDescription(data.description || "")
        setLogoUrl(data.logo_image || "")
        setBannerUrl(data.banner_image || "")
      }
    } catch (error) {
      console.error("Failed to fetch website details", error)
    }
  }

  const handleEmailConfigChange = (field: string, value: string) => {
    setEmailConfig((prev) => ({ ...prev, [field]: value }))
  }

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setEmailLoading(true)
    try {
      await apiFetch("settings/email-config", {
        method: "POST",
        body: JSON.stringify(emailConfig),
      })
      toast.success("Email configuration saved successfully")
    } catch (error: any) {
      console.error(error)
      toast.error(error.message || "Failed to save email configuration")
    } finally {
      setEmailLoading(false)
    }
  }

  const handleDetailsSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setDetailsLoading(true)
    try {
      const formData = new FormData()
      formData.append("title", title)
      formData.append("description", description)
      if (logo) formData.append("logo_image", logo)
      if (banner) formData.append("banner_image", banner)

      const response = await apiFetch<any>("settings/website-details", {
        method: "POST",
        body: formData,
      })
      if (response.data) {
        setLogoUrl(response.data.logo_image || logoUrl)
        setBannerUrl(response.data.banner_image || bannerUrl)
        setLogo(null)
        setBanner(null)
      }
      toast.success("Website details saved successfully")
    } catch (error: any) {
      console.error(error)
      toast.error(error.message || "Failed to save website details")
    } finally {
      setDetailsLoading(false)
    }
  }

  const baseUrl =
    import.meta.env.VITE_BACKEND_URL ||
    import.meta.env.VITE_BASE_URL?.replace("/api", "")

  const getImageUrl = (path: string) => {
    if (!path) return ""
    if (path.startsWith("http")) return path
    return `${baseUrl}/storage/${path}`
  }

  return (
    <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 4 }}>
      <Typography variant="h5" sx={{ fontWeight: "bold" }}>
        Website Configuration
      </Typography>

      <Card sx={{ maxWidth: 800 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Website Details
          </Typography>
          <form onSubmit={handleDetailsSubmit}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <TextField
                label="Website Title"
                type="text"
                fullWidth
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter website title"
              />
              <TextField
                label="Website Description"
                type="text"
                fullWidth
                multiline
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter website description"
              />

              <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                <Box sx={{ flex: 1, minWidth: 250 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, color: "text.secondary" }}
                  >
                    Logo Image
                  </Typography>
                  <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                    sx={{ mb: 2 }}
                  >
                    Upload Logo
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={(e) =>
                        setLogo(e.target.files ? e.target.files[0] : null)
                      }
                    />
                  </Button>
                  {logo ? (
                    <Typography variant="body2" color="success.main">
                      Selected file: {logo.name}
                    </Typography>
                  ) : logoUrl ? (
                    <Box mt={1}>
                      <img
                        src={getImageUrl(logoUrl)}
                        alt="Logo"
                        style={{
                          maxHeight: 100,
                          maxWidth: "100%",
                          objectFit: "contain",
                        }}
                      />
                    </Box>
                  ) : null}
                </Box>

                <Box sx={{ flex: 1, minWidth: 250 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, color: "text.secondary" }}
                  >
                    Banner Image
                  </Typography>
                  <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                    sx={{ mb: 2 }}
                  >
                    Upload Banner
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={(e) =>
                        setBanner(e.target.files ? e.target.files[0] : null)
                      }
                    />
                  </Button>
                  {banner ? (
                    <Typography variant="body2" color="success.main">
                      Selected file: {banner.name}
                    </Typography>
                  ) : bannerUrl ? (
                    <Box mt={1}>
                      <img
                        src={getImageUrl(bannerUrl)}
                        alt="Banner"
                        style={{
                          maxHeight: 100,
                          maxWidth: "100%",
                          objectFit: "cover",
                          borderRadius: 4,
                        }}
                      />
                    </Box>
                  ) : null}
                </Box>
              </Box>

              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={detailsLoading}
                sx={{ mt: 1, alignSelf: "flex-start", textTransform: "none" }}
              >
                {detailsLoading ? "Saving..." : "Save Website Details"}
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>

      <Card sx={{ maxWidth: 800 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Email Settings
          </Typography>
          <form onSubmit={handleEmailSubmit}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <TextField
                  label="From Email Address"
                  type="email"
                  fullWidth
                  value={emailConfig.from_address}
                  onChange={(e) => handleEmailConfigChange("from_address", e.target.value)}
                  required
                  placeholder="noreply@example.com"
                  sx={{ flex: 1, minWidth: "250px" }}
                />
                <TextField
                  label="Mailer (e.g., smtp)"
                  type="text"
                  fullWidth
                  value={emailConfig.mailer}
                  onChange={(e) => handleEmailConfigChange("mailer", e.target.value)}
                  required
                  sx={{ flex: 1, minWidth: "150px" }}
                />
              </Box>
              
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <TextField
                  label="Host"
                  type="text"
                  fullWidth
                  value={emailConfig.host}
                  onChange={(e) => handleEmailConfigChange("host", e.target.value)}
                  required
                  placeholder="smtp.mailtrap.io"
                  sx={{ flex: 1, minWidth: "300px" }}
                />
                <TextField
                  label="Port"
                  type="text"
                  fullWidth
                  value={emailConfig.port}
                  onChange={(e) => handleEmailConfigChange("port", e.target.value)}
                  required
                  sx={{ flex: 1, minWidth: "80px" }}
                />
              </Box>

              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <TextField
                  label="Username"
                  type="text"
                  fullWidth
                  value={emailConfig.username}
                  onChange={(e) => handleEmailConfigChange("username", e.target.value)}
                  required
                  sx={{ flex: 1, minWidth: "250px" }}
                />
                <TextField
                  label="Password"
                  type="text"
                  fullWidth
                  value={emailConfig.password}
                  onChange={(e) => handleEmailConfigChange("password", e.target.value)}
                  required
                  sx={{ flex: 1, minWidth: "250px" }}
                />
              </Box>

              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <TextField
                  label="Encryption (tls, ssl)"
                  type="text"
                  fullWidth
                  value={emailConfig.encryption}
                  onChange={(e) => handleEmailConfigChange("encryption", e.target.value)}
                  sx={{ flex: 1, minWidth: "200px" }}
                />
              </Box>

              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={emailLoading}
                sx={{ mt: 1, alignSelf: "flex-start", textTransform: "none" }}
              >
                {emailLoading ? "Saving..." : "Save Email Configuration"}
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  )
}

export default WebsiteConfig
